import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import { 
  Car, 
  Send, 
  Trash2,
  ArrowLeft,
  MessageSquare,
  Loader2,
  CheckCircle,
  User,
  Bot,
  Sparkles,
  MapPin,
  Calendar,
  Phone,
  ArrowLeftRight,
  Plane
} from 'lucide-react';

const BookingChat = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [bookingComplete, setBookingComplete] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Quick booking form state
  const [formData, setFormData] = useState({
    pickup_location: '',
    dropoff_location: '',
    date_time: '',
    phone_number: '',
    return_journey: false,
    return_date_time: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (showChat && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hello! Welcome to NeoRide. I'm your AI booking assistant. I can help you book a taxi or private hire journey anywhere in Wolverhampton and beyond.\n\nHow can I help you today? Would you like to:\n• Book a regular journey\n• Book an airport transfer\n• Ask about our services"
      }]);
    }
  }, [showChat, messages.length]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pickup_location || !formData.dropoff_location || !formData.date_time || !formData.phone_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    setFormSubmitting(true);

    try {
      // Parse date and time
      const dateTime = new Date(formData.date_time);
      const journeyDate = dateTime.toISOString().split('T')[0];
      const pickupTime = dateTime.toTimeString().slice(0, 5);

      // Create main booking
      const bookingData = {
        full_name: user?.name || 'Guest',
        contact_number: formData.phone_number,
        email: user?.email || 'guest@neoride.com',
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        journey_date: journeyDate,
        pickup_time: pickupTime,
        passengers: 1,
        additional_requirements: formData.return_journey ? `Return journey requested for ${formData.return_date_time}` : null,
        is_airport_transfer: formData.pickup_location.toLowerCase().includes('airport') || formData.dropoff_location.toLowerCase().includes('airport'),
        is_guest: !user,
        user_id: user?.user_id || null
      };

      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) throw new Error('Booking failed');

      const booking = await response.json();
      
      // If return journey, create second booking
      if (formData.return_journey && formData.return_date_time) {
        const returnDateTime = new Date(formData.return_date_time);
        const returnDate = returnDateTime.toISOString().split('T')[0];
        const returnTime = returnDateTime.toTimeString().slice(0, 5);

        const returnBookingData = {
          ...bookingData,
          pickup_location: formData.dropoff_location,
          dropoff_location: formData.pickup_location,
          journey_date: returnDate,
          pickup_time: returnTime,
          additional_requirements: `Return journey for booking ${booking.booking_id}`
        };

        await fetch(`${API}/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(returnBookingData)
        });
      }

      setBookingComplete(booking);
      toast.success('Booking submitted!', {
        description: `Reference: ${booking.booking_id}${formData.return_journey ? ' (+ return journey)' : ''}`
      });

      // Reset form
      setFormData({
        pickup_location: '',
        dropoff_location: '',
        date_time: '',
        phone_number: '',
        return_journey: false,
        return_date_time: ''
      });

    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);

      if (data.booking_data) {
        if (data.booking_data.type === 'driver_signup') {
          toast.success('Driver application submitted!', {
            description: `Reference: ${data.booking_data.driver_id}`
          });
        } else if (data.booking_data.booking_id) {
          setBookingComplete(data.booking_data);
          toast.success('Booking submitted!', {
            description: `Reference: ${data.booking_data.booking_id}`
          });
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message. Please try again.');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble connecting right now. Please try again or email us at neorideai@gmail.com." 
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = async () => {
    try {
      await fetch(`${API}/chat/${sessionId}`, { method: 'DELETE' });
      setMessages([{
        role: 'assistant',
        content: "Chat cleared. How can I help you with your booking today?"
      }]);
      setBookingComplete(null);
      toast.success('Chat cleared');
    } catch (error) {
      console.error('Clear chat error:', error);
    }
  };

  // Get minimum datetime (now + 1 hour)
  const getMinDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return now.toISOString().slice(0, 16);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#1e3a5f] text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              className="text-white/80 hover:text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Car className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">NEORIDE</p>
                <p className="text-xs text-white/70">Airport Transfers & Private Hire</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {!user && (
              <Button 
                onClick={login}
                size="sm"
                className="bg-white text-[#1e3a5f] hover:bg-white/90"
                data-testid="login-btn"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Quick Booking Form */}
      <div className="pt-20 pb-4 px-4 bg-gradient-to-b from-[#1e3a5f] to-[#2d4a6f]">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6" data-testid="quick-booking-form">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Book Your Ride</h2>
            
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Pickup Location</Label>
                <Input
                  name="pickup_location"
                  value={formData.pickup_location}
                  onChange={handleFormChange}
                  placeholder="Pickup Location"
                  className="border-slate-300"
                  required
                  data-testid="input-pickup"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Drop-off Location</Label>
                <Input
                  name="dropoff_location"
                  value={formData.dropoff_location}
                  onChange={handleFormChange}
                  placeholder="Drop-off Location"
                  className="border-slate-300"
                  required
                  data-testid="input-dropoff"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Date & Time</Label>
                <Input
                  type="datetime-local"
                  name="date_time"
                  value={formData.date_time}
                  onChange={handleFormChange}
                  min={getMinDateTime()}
                  className="border-slate-300"
                  required
                  data-testid="input-datetime"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Phone Number</Label>
                <Input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleFormChange}
                  placeholder="Phone Number"
                  className="border-slate-300"
                  required
                  data-testid="input-phone"
                />
              </div>

              {/* Return Journey Option */}
              <div className="flex items-center gap-3 py-2">
                <Checkbox
                  id="return_journey"
                  checked={formData.return_journey}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, return_journey: checked }))}
                  className="border-[#1e3a5f] data-[state=checked]:bg-[#1e3a5f]"
                />
                <Label htmlFor="return_journey" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Book Return Journey
                </Label>
              </div>
              
              {formData.return_journey && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Return Date & Time</Label>
                  <Input
                    type="datetime-local"
                    name="return_date_time"
                    value={formData.return_date_time}
                    onChange={handleFormChange}
                    min={formData.date_time || getMinDateTime()}
                    className="border-slate-300"
                    required={formData.return_journey}
                    data-testid="input-return-datetime"
                  />
                </div>
              )}

              <Button 
                type="submit"
                disabled={formSubmitting}
                className="w-full bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white py-6 text-lg font-semibold rounded-xl"
                data-testid="submit-booking-btn"
              >
                {formSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'BOOK NOW'
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-slate-500 text-sm">Online Booking Only - No Phone Bookings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Airport Transfers Info */}
      <div className="py-6 px-4 bg-white border-y border-slate-200">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-lg font-bold text-slate-900 mb-3">Airport Transfers</h3>
          <p className="text-sm text-slate-600 mb-4">We cover all major UK airports:</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            {['Birmingham', 'Manchester', 'Heathrow', 'Gatwick', 'Luton', 'East Midlands'].map((airport) => (
              <span key={airport} className="flex items-center gap-1 text-[#1e3a5f]">
                <Plane className="w-4 h-4" />
                {airport}
              </span>
            ))}
          </div>
          <p className="mt-3 text-sm font-medium text-slate-700 italic">On-Time Service • Fixed Rates</p>
        </div>
      </div>

      {/* AI Chat Section */}
      <div className="flex-1 px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {!showChat ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center mx-auto mb-4">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Prefer to Chat?</h3>
              <p className="text-slate-600 mb-4">Our AI assistant can help you with complex bookings, special requests, or any questions.</p>
              <Button 
                onClick={() => setShowChat(true)}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
                data-testid="start-chat-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Chat with AI Assistant
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-white" />
                  <span className="text-white font-medium">AI Booking Assistant</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={clearChat}
                  className="text-white/80 hover:text-white hover:bg-white/10"
                  data-testid="clear-chat-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <ScrollArea className="h-[300px]">
                <div className="p-4 space-y-4">
                  {messages.map((message, index) => (
                    <div 
                      key={index}
                      className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`chat-message-${index}`}
                    >
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                      )}
                      
                      <div 
                        className={`max-w-[85%] p-3 rounded-2xl ${
                          message.role === 'user' 
                            ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-tr-sm' 
                            : 'bg-slate-100 text-slate-700 rounded-tl-sm'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                      
                      {message.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex gap-3 justify-start" data-testid="typing-indicator">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-slate-100 p-3 rounded-2xl rounded-tl-sm">
                        <div className="flex gap-1.5">
                          <div className="typing-dot" />
                          <div className="typing-dot" />
                          <div className="typing-dot" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              
              <div className="p-4 border-t border-slate-200">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    className="flex-1 border-slate-300"
                    disabled={isLoading}
                    data-testid="chat-input"
                  />
                  <Button 
                    onClick={sendMessage}
                    disabled={!input.trim() || isLoading}
                    className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
                    data-testid="send-btn"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booking Complete Banner */}
      {bookingComplete && (
        <div className="fixed bottom-4 left-4 right-4 z-40" data-testid="booking-complete-banner">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-lg border border-emerald-200">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="text-slate-900 font-medium">Booking Submitted!</p>
                <p className="text-sm text-slate-500">Reference: {bookingComplete.booking_id}</p>
                <p className="text-xs text-slate-400">You will receive an email confirmation</p>
              </div>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => setBookingComplete(null)}
                className="text-slate-500"
              >
                ✕
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-100 py-4 px-4 text-center text-xs text-slate-600">
        <p>Pre-Booked Private Hire Only • No Street Pickups</p>
        <p className="mt-1">Awaiting Licence from Wolverhampton City Council</p>
      </footer>
    </div>
  );
};

export default BookingChat;
